// components/Bot/botStatus.js
//
// One vocabulary for the `trading_bots.status` column, shared by every screen
// that colours or labels a bot.
//
// WHY THIS EXISTS
// ---------------
// The scanner in crypto-backend-trade writes three statuses the frontend had
// never seen: `spawning` (the spawner has created the row but the bot is not
// yet executing), `closing` (the reaper is flattening it) and `failed` (the
// spawn or the run aborted, with the reason in `error_message`). Before this
// module every consumer either compared against the two strings it happened to
// know — `bot.status === 'active'` in Trader_Form, `status === 'terminated'` in
// the scanner page — or fell through to a default. The result was that a
// half-spawned bot rendered as a confident green "Active" and a `failed` bot
// rendered green too, with its reason nowhere on screen.
//
// `tone` is an MUI severity/colour name so it drops straight into <Chip color>
// and <Alert severity>. Callers that paint their own hex map it themselves.
//
// NOTE ON Trader_Form: that page overwrites `bot.status` with a derived
// 'paused' | 'active' (from stop_trade_ASSET) at fetch time and keeps the
// server's real status in `bot.statusMessage`. Use `resolveBotStatus(bot)`
// there rather than reading one field.

export const IN_TRANSITION_STATUSES = ['spawning', 'closing'];

const META = {
  spawning: {
    label: 'Spawning',
    tone: 'info',
    inTransition: true,
    description: 'The scanner is starting this bot. It is not trading yet.',
  },
  closing: {
    label: 'Closing',
    tone: 'warning',
    inTransition: true,
    description: 'Positions are being flattened and the bot is shutting down.',
  },
  failed: {
    label: 'Failed',
    tone: 'error',
    inTransition: false,
    description: 'This bot stopped with an error and is not trading.',
  },
  terminated: {
    label: 'Terminated',
    tone: 'default',
    inTransition: false,
    description: 'This bot has finished and is no longer running.',
  },
  paused: {
    label: 'Paused',
    tone: 'default',
    inTransition: false,
    description: 'Trading is paused. Resume it to continue.',
  },
  active: {
    label: 'Active',
    tone: 'success',
    inTransition: false,
    description: 'Running and evaluating its strategy.',
  },
  running: {
    label: 'Running',
    tone: 'success',
    inTransition: false,
    description: 'Running and evaluating its strategy.',
  },
  // Not a trading_bots.status value: the trade engine's futures preflight
  // refusal (api `futures_block`, from botStats:{id}) — see futuresBlockOf.
  blocked: {
    label: 'Blocked',
    tone: 'error',
    inTransition: false,
    description: 'The engine refuses every entry of this bot.',
  },
  scanner_profile: {
    label: 'Scanner',
    tone: 'info',
    inTransition: false,
    description: 'A scanner profile — it spawns bots rather than trading itself.',
  },
};

/**
 * The trade engine's live futures refusal on a bot row, or null.
 *
 * crypto-backend-trade refuses a futures bot's entries (POSITION_MODE_MISMATCH
 * when the Binance account's Hedge / One-way mode is not the bot's,
 * MAX_LEVERAGE …) and records it in Redis only — never on the DB row, and its
 * one socket publish is missed by any page that was not open. The api's bot
 * lists now read it back as `futures_block: { code, reason, at }`, so it is
 * live engine state and is trusted (unlike a row's stale error_message).
 */
export function futuresBlockOf(bot) {
  const b = bot && (bot.futures_block || bot.futuresBlock);
  return b && b.code ? b : null;
}

/**
 * The block a `botUpdate:<id>` socket event says, from the botStats hash the
 * engine's updateStateAndBroadcast sends (`data.stats` / `data.botData.stats`,
 * HGETALL botStats:{id}, so the futuresBlock* fields ride along):
 *   { code, reason, at }  the hash has futuresBlockCode
 *   null                  a full stats hash without it (cleared)
 *   undefined             nothing to say: no stats, or a liveMark tick
 *                         (file/liveMark.js publishes only a price subset)
 * Never read from `error_message === ''`: every broadcast defaults it to ''.
 */
export function futuresBlockFromSocket(data) {
  if (!data || data.liveMark) return undefined;
  let stats = data.stats || (data.botData && data.botData.stats);
  if (typeof stats === 'string') {
    try { stats = JSON.parse(stats); } catch (_) { return undefined; }
  }
  if (!stats || typeof stats !== 'object' || Array.isArray(stats) || !Object.keys(stats).length) return undefined;
  if (!stats.futuresBlockCode) return null;
  return { code: String(stats.futuresBlockCode), reason: stats.futuresBlockReason || null, at: stats.futuresBlockAt || null };
}

/**
 * Short words for a block: "account in Hedge mode", "leverage above your
 * limit", else the code in words. For "Bots blocked: <words>".
 */
export function futuresBlockWords(block) {
  if (!block || !block.code) return '';
  const code = String(block.code).toUpperCase();
  const reason = String(block.reason || '');
  if (code === 'POSITION_MODE_MISMATCH') {
    const m = reason.match(/account is in (Hedge|One-way) mode/i);
    if (m) return `account in ${m[1].toLowerCase() === 'hedge' ? 'Hedge' : 'One-way'} mode`;
    return 'position mode mismatch';
  }
  if (code === 'MAX_LEVERAGE') return 'leverage above your limit';
  return humanize(code).toLowerCase();
}

/** Normalize whatever the row/socket gave us into a lowercase key. */
function keyOf(raw) {
  if (raw == null) return '';
  return String(raw).trim().toLowerCase();
}

/**
 * Metadata for a raw status string.
 *
 * @param {string} raw            the server's status value
 * @param {object} [opts]
 * @param {string} [opts.errorMessage]  shown as the description for `failed`
 * @param {object} [opts.futuresBlock]  the row's `futures_block`: the bot
 *        reads as `blocked` (error) with the engine's reason, unless it has
 *        already failed or finished (or is a scanner profile)
 * @returns {{key,label,tone,inTransition,description,known}}
 */
export function getBotStatusMeta(raw, opts = {}) {
  const key = keyOf(raw);
  const block = opts.futuresBlock && opts.futuresBlock.code ? opts.futuresBlock : null;
  if (block && key !== 'failed' && key !== 'terminated' && key !== 'scanner_profile') {
    return { key: 'blocked', ...META.blocked, description: block.reason ? String(block.reason) : META.blocked.description, known: true };
  }
  const hit = META[key];
  if (!hit) {
    // An unknown status is still shown — hiding it is how a new backend state
    // becomes invisible instead of merely unstyled.
    return {
      key,
      label: key ? humanize(key) : 'Unknown',
      tone: 'default',
      inTransition: false,
      description: '',
      known: false,
    };
  }
  const description =
    key === 'failed' && opts.errorMessage
      ? String(opts.errorMessage)
      : hit.description;
  return { key, ...hit, description, known: true };
}

/** `max_daily_loss` / `someState` -> `Max Daily Loss` / `Some State`. */
function humanize(s) {
  return String(s)
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Pick the status that should be SHOWN for a bot object as Trader_Form shapes
 * it, where `status` has already been flattened to paused/active and the real
 * server status survives in `statusMessage`.
 *
 * The transitional and failed states win over the derived paused/active,
 * because "this bot is still spawning" is the more important fact than "its
 * stop flag is currently false".
 */
export function resolveBotStatus(bot) {
  if (!bot) return '';
  const server = keyOf(bot.statusMessage ?? bot.status);
  if (server === 'spawning' || server === 'closing' || server === 'failed' || server === 'terminated') {
    return server;
  }
  return keyOf(bot.status) || server;
}

/** True while the bot is mid-transition — actions should be disabled. */
export function isBotInTransition(bot) {
  return IN_TRANSITION_STATUSES.includes(resolveBotStatus(bot));
}

const botStatus = {
  getBotStatusMeta,
  futuresBlockOf,
  futuresBlockWords,
  futuresBlockFromSocket,
  resolveBotStatus,
  isBotInTransition,
  IN_TRANSITION_STATUSES,
};

export default botStatus;
